// src/components/ServiceManager.js
//
// Settings -> Services. Admins and managers can add, edit, retire and restore
// services.
//
// Removing a service that has been billed would cascade into its bill lines
// and rewrite past reports, so the server archives those instead of deleting
// them. Archived services vanish from the billing and booking menus while
// every historical bill stays exactly as it was. A service that was never
// used is deleted outright.

import React, { useEffect, useMemo, useState } from "react";
import { fetchWithAuth } from "../config/api";

const BLANK_NEW = {
  name: "",
  price: "",
  duration: "30",
  catalog_id: "",
  is_flexible_price: false,
};

export default function ServiceManager() {
  const [services, setServices] = useState([]);
  const [catalogs, setCatalogs] = useState([]);
  const [draft, setDraft] = useState({});

  const [loading, setLoading] = useState(true);
  const [savingId, setSavingId] = useState(null);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");

  const [search, setSearch] = useState("");
  const [catalogFilter, setCatalogFilter] = useState("");
  const [showArchived, setShowArchived] = useState(false);

  const [showAdd, setShowAdd] = useState(false);
  const [newService, setNewService] = useState(BLANK_NEW);
  const [adding, setAdding] = useState(false);

  async function load() {
    setLoading(true);
    setError("");
    try {
      const res = await fetchWithAuth("/api/manage/services/");
      const js = await res.json().catch(() => ({}));
      if (!res.ok) {
        setError(js.error || "Could not load services.");
        return;
      }
      setServices(js.services || []);
      setCatalogs(js.catalogs || []);
      setDraft({});
    } catch (e) {
      setError("Could not reach the server.");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function edit(id, field, value) {
    setError("");
    setNotice("");
    setDraft((prev) => ({ ...prev, [id]: { ...(prev[id] || {}), [field]: value } }));
  }

  function valueOf(row, field) {
    const edited = draft[row.id];
    return edited && field in edited ? edited[field] : row[field];
  }

  const dirtyIds = useMemo(
    () =>
      new Set(
        Object.entries(draft)
          .filter(([id, fields]) => {
            const row = services.find((s) => String(s.id) === String(id));
            if (!row) return false;
            return Object.entries(fields).some(([k, v]) => String(v) !== String(row[k]));
          })
          .map(([id]) => Number(id))
      ),
    [draft, services]
  );

  const visible = useMemo(() => {
    const term = search.trim().toLowerCase();
    return services.filter((s) => {
      if (!showArchived && !s.is_active) return false;
      if (catalogFilter && String(s.catalog_id || "") !== String(catalogFilter)) return false;
      if (term && !s.name.toLowerCase().includes(term)) return false;
      return true;
    });
  }, [services, search, catalogFilter, showArchived]);

  const archivedCount = useMemo(
    () => services.filter((s) => !s.is_active).length,
    [services]
  );

  async function saveRow(row) {
    const fields = draft[row.id] || {};
    const payload = {};
    ["name", "price", "duration", "catalog_id", "is_flexible_price"].forEach((k) => {
      if (k in fields && String(fields[k]) !== String(row[k])) payload[k] = fields[k];
    });
    if (!Object.keys(payload).length) return;

    setSavingId(row.id);
    setError("");
    setNotice("");
    try {
      const res = await fetchWithAuth(`/api/manage/services/${row.id}/`, {
        method: "PATCH",
        body: JSON.stringify(payload),
      });
      const js = await res.json().catch(() => ({}));
      if (!res.ok) {
        setError(js.error || "Could not save.");
        return;
      }
      setServices((prev) => prev.map((s) => (s.id === row.id ? js : s)));
      setDraft((prev) => {
        const copy = { ...prev };
        delete copy[row.id];
        return copy;
      });
      setNotice(`Saved ${js.name}.`);
    } finally {
      setSavingId(null);
    }
  }

  async function setActive(row, active) {
    setSavingId(row.id);
    setError("");
    setNotice("");
    try {
      const res = await fetchWithAuth(`/api/manage/services/${row.id}/`, {
        method: "PATCH",
        body: JSON.stringify({ is_active: active }),
      });
      const js = await res.json().catch(() => ({}));
      if (!res.ok) {
        setError(js.error || "Could not update.");
        return;
      }
      setServices((prev) => prev.map((s) => (s.id === row.id ? js : s)));
      setNotice(active ? `${js.name} is back in the menu.` : `${js.name} archived.`);
    } finally {
      setSavingId(null);
    }
  }

  async function removeService(row) {
    const used = row.in_use;
    const message = used
      ? `"${row.name}" is already used on ${row.usage.bill_lines} bill line(s).\n\n` +
        "It will be archived, not deleted, so past bills and reports stay correct. " +
        "It will stop appearing when billing and booking.\n\nContinue?"
      : `"${row.name}" has never been used. It will be permanently deleted.\n\nContinue?`;
    if (!window.confirm(message)) return;

    setSavingId(row.id);
    setError("");
    setNotice("");
    try {
      const res = await fetchWithAuth(`/api/manage/services/${row.id}/`, { method: "DELETE" });
      const js = await res.json().catch(() => ({}));
      if (!res.ok) {
        setError(js.error || "Could not remove the service.");
        return;
      }
      if (js.status === "deleted") {
        setServices((prev) => prev.filter((s) => s.id !== row.id));
      } else {
        setServices((prev) => prev.map((s) => (s.id === row.id ? js.service : s)));
      }
      setNotice(js.message || "Done.");
    } finally {
      setSavingId(null);
    }
  }

  async function addService() {
    setError("");
    setNotice("");
    if (!newService.name.trim()) {
      setError("Service name is required.");
      return;
    }
    if (newService.price === "" || Number(newService.price) < 0) {
      setError("Enter a price of zero or more.");
      return;
    }
    setAdding(true);
    try {
      const res = await fetchWithAuth("/api/manage/services/", {
        method: "POST",
        body: JSON.stringify({
          ...newService,
          price: Number(newService.price),
          duration: Number(newService.duration || 30),
          catalog_id: newService.catalog_id || null,
        }),
      });
      const js = await res.json().catch(() => ({}));
      if (!res.ok) {
        setError(js.error || "Could not add the service.");
        return;
      }
      setServices((prev) => [...prev, js]);
      setNewService(BLANK_NEW);
      setShowAdd(false);
      setNotice(`${js.name} added.`);
    } finally {
      setAdding(false);
    }
  }

  const cell = { padding: "6px 8px", verticalAlign: "middle" };

  return (
    <div>
      <div style={{ display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap", marginBottom: 12 }}>
        <h4 style={{ margin: 0 }}>Services</h4>
        <button className="btn" type="button" onClick={() => setShowAdd((v) => !v)}>
          {showAdd ? "Cancel" : "+ Add service"}
        </button>
        <button className="btn" type="button" onClick={load} disabled={loading}>
          {loading ? "Loading..." : "Refresh"}
        </button>
      </div>

      {error && (
        <div style={{ background: "#fdecea", border: "1px solid #f5a29b", color: "#8a1c12",
                      borderRadius: 8, padding: "8px 12px", marginBottom: 10 }}>
          {error}
        </div>
      )}
      {notice && (
        <div style={{ background: "#e8f5e9", border: "1px solid #8ccf91", color: "#1b5e20",
                      borderRadius: 8, padding: "8px 12px", marginBottom: 10 }}>
          {notice}
        </div>
      )}

      {showAdd && (
        <div style={{ border: "1px solid #eee", borderRadius: 10, padding: 12, marginBottom: 14 }}>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "flex-end" }}>
            <label style={{ display: "grid", gap: 4 }}>
              <span style={{ fontSize: 12 }}>Name</span>
              <input className="input" value={newService.name}
                     onChange={(e) => setNewService({ ...newService, name: e.target.value })} />
            </label>
            <label style={{ display: "grid", gap: 4 }}>
              <span style={{ fontSize: 12 }}>Category</span>
              <select className="input" value={newService.catalog_id}
                      onChange={(e) => setNewService({ ...newService, catalog_id: e.target.value })}>
                <option value="">Uncategorised</option>
                {catalogs.map((c) => (
                  <option key={c.id} value={c.id}>{c.name}</option>
                ))}
              </select>
            </label>
            <label style={{ display: "grid", gap: 4 }}>
              <span style={{ fontSize: 12 }}>Price (AED)</span>
              <input className="input" type="number" min="0" step="0.01" style={{ maxWidth: 120 }}
                     value={newService.price}
                     onChange={(e) => setNewService({ ...newService, price: e.target.value })} />
            </label>
            <label style={{ display: "grid", gap: 4 }}>
              <span style={{ fontSize: 12 }}>Minutes</span>
              <input className="input" type="number" min="1" step="1" style={{ maxWidth: 90 }}
                     value={newService.duration}
                     onChange={(e) => setNewService({ ...newService, duration: e.target.value })} />
            </label>
            <label style={{ display: "flex", gap: 6, alignItems: "center", paddingBottom: 6 }}>
              <input type="checkbox" checked={newService.is_flexible_price}
                     onChange={(e) => setNewService({ ...newService, is_flexible_price: e.target.checked })} />
              <span style={{ fontSize: 13 }}>Price can be changed when billing</span>
            </label>
            <button className="btn" type="button" onClick={addService} disabled={adding}>
              {adding ? "Adding..." : "Add"}
            </button>
          </div>
        </div>
      )}

      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center", marginBottom: 10 }}>
        <input className="input" placeholder="Search by name" value={search}
               style={{ maxWidth: 220 }} onChange={(e) => setSearch(e.target.value)} />
        <select className="input" value={catalogFilter} onChange={(e) => setCatalogFilter(e.target.value)}>
          <option value="">All categories</option>
          {catalogs.map((c) => (
            <option key={c.id} value={c.id}>{c.name}</option>
          ))}
        </select>
        <label style={{ display: "flex", gap: 6, alignItems: "center", fontSize: 13 }}>
          <input type="checkbox" checked={showArchived}
                 onChange={(e) => setShowArchived(e.target.checked)} />
          Show archived ({archivedCount})
        </label>
        <span style={{ color: "#666", fontSize: 13 }}>
          {visible.length} of {services.length} shown
        </span>
      </div>

      {loading && <div>Loading services...</div>}

      {!loading && visible.length === 0 && <div>No services match.</div>}

      {!loading && visible.length > 0 && (
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 900 }}>
            <thead>
              <tr style={{ borderBottom: "2px solid #eee", textAlign: "left" }}>
                <th style={cell}>Name</th>
                <th style={cell}>Category</th>
                <th style={cell}>Price</th>
                <th style={cell}>Minutes</th>
                <th style={cell}>Flexible</th>
                <th style={cell}>Used</th>
                <th style={cell}></th>
              </tr>
            </thead>
            <tbody>
              {visible.map((row) => {
                const dirty = dirtyIds.has(row.id);
                return (
                  <tr key={row.id}
                      style={{ borderBottom: "1px solid #f3f3f3", opacity: row.is_active ? 1 : 0.55 }}>
                    <td style={cell}>
                      <input className="input" value={valueOf(row, "name")} style={{ minWidth: 170 }}
                             onChange={(e) => edit(row.id, "name", e.target.value)} />
                      {!row.is_active && (
                        <span style={{ marginLeft: 6, fontSize: 11, background: "#eee",
                                       borderRadius: 6, padding: "2px 6px" }}>archived</span>
                      )}
                    </td>
                    <td style={cell}>
                      <select className="input" value={valueOf(row, "catalog_id") || ""}
                              onChange={(e) => edit(row.id, "catalog_id", e.target.value)}>
                        <option value="">Uncategorised</option>
                        {catalogs.map((c) => (
                          <option key={c.id} value={c.id}>{c.name}</option>
                        ))}
                      </select>
                    </td>
                    <td style={cell}>
                      <input className="input" type="number" min="0" step="0.01" style={{ maxWidth: 100 }}
                             value={valueOf(row, "price")}
                             onChange={(e) => edit(row.id, "price", e.target.value)} />
                    </td>
                    <td style={cell}>
                      <input className="input" type="number" min="1" step="1" style={{ maxWidth: 80 }}
                             value={valueOf(row, "duration")}
                             onChange={(e) => edit(row.id, "duration", e.target.value)} />
                    </td>
                    <td style={cell}>
                      <input type="checkbox" checked={Boolean(valueOf(row, "is_flexible_price"))}
                             onChange={(e) => edit(row.id, "is_flexible_price", e.target.checked)} />
                    </td>
                    <td style={{ ...cell, fontSize: 12, color: "#666", whiteSpace: "nowrap" }}>
                      {row.usage ? `${row.usage.bill_lines} bill lines` : "-"}
                    </td>
                    <td style={{ ...cell, whiteSpace: "nowrap" }}>
                      <button className="btn" type="button" onClick={() => saveRow(row)}
                              disabled={!dirty || savingId === row.id}>
                        {savingId === row.id ? "Saving..." : "Save"}
                      </button>{" "}
                      {row.is_active ? (
                        <button className="btn" type="button" onClick={() => removeService(row)}
                                disabled={savingId === row.id}>
                          {row.in_use ? "Archive" : "Delete"}
                        </button>
                      ) : (
                        <button className="btn" type="button" onClick={() => setActive(row, true)}
                                disabled={savingId === row.id}>
                          Restore
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <div style={{ fontSize: 12, color: "#666", marginTop: 12, lineHeight: 1.6 }}>
        <div><strong>Archive</strong> - a service that has already been billed cannot be deleted
          without taking its bill lines with it, so it is archived. It stops appearing when
          billing and booking, and every past bill stays exactly as it was.</div>
        <div><strong>Delete</strong> - only offered for a service that has never been used.</div>
      </div>
    </div>
  );
}
